/**
 * ChatInputAreaWithUpload Component
 *
 * Enhanced input area that includes file upload support for documents and images.
 * Extends ChatInputArea with file attachment functionality.
 */

import React from 'react';
import { Upload, message } from 'antd';
import getFileTypeIcon from '../../utils/getFileTypeIcon';

const ChatInputAreaWithUpload = ({
  value = '',
  onChange,
  onSend,
  disabled = false,
  placeholder = 'Ask about your reports data...',
  attachedFiles = [],
  onFilesChange,
  acceptedFileTypes = '.pdf,.docx,.html,.txt,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.gif,.webp,.svg',
  maxFileSize = 10 * 1024 * 1024, // 10MB
  blobUrlsRef = null
}) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (onSend && (value.trim() || attachedFiles.length > 0) && !disabled) {
        onSend();
      }
    }
  };

  const handleSendClick = () => {
    if (onSend && (value.trim() || attachedFiles.length > 0) && !disabled) {
      onSend();
    }
  };

  const handleBeforeUpload = (file) => {
    // Image types
    const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];

    // Document types
    const documentTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/html',
      'text/plain',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    const documentExtensions = ['.pdf', '.docx', '.html', '.txt', '.xlsx', '.xls', '.csv'];

    const isImage = imageTypes.includes(file.type) ||
                    imageExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    const isDocument = documentTypes.includes(file.type) ||
                       documentExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

    if (!isImage && !isDocument) {
      message.error(`${file.name}: Only PDF, DOCX, HTML, TXT, Excel, CSV, JPG, PNG, GIF, WebP, and SVG files are supported`);
      return false;
    }

    // Validate file size
    if (file.size > maxFileSize) {
      message.error(`${file.name}: File must be smaller than ${maxFileSize / (1024 * 1024)}MB`);
      return false;
    }

    // Create blob URL for images
    if (isImage && blobUrlsRef) {
      const blobUrl = URL.createObjectURL(file);
      blobUrlsRef.current.set(file, blobUrl);
    }

    // Add file to attachedFiles
    if (onFilesChange) {
      onFilesChange([...attachedFiles, file]);
    }

    // Prevent auto upload
    return false;
  };

  const handleRemoveFile = (fileToRemove) => {
    // Revoke blob URL if it's an image
    if (fileToRemove.type.startsWith('image/') && blobUrlsRef) {
      const url = blobUrlsRef.current.get(fileToRemove);
      if (url) {
        URL.revokeObjectURL(url);
        blobUrlsRef.current.delete(fileToRemove);
      }
    }

    if (onFilesChange) {
      onFilesChange(attachedFiles.filter(f => f !== fileToRemove));
    }
  };

  return (
    <div className="reports-chatbot-input">
      <div className="reports-chatbot-input-wrapper">
        {/* File attachments display inside input */}
        {attachedFiles.length > 0 && (
          <div className="chatbot-attachments-list">
            {attachedFiles.map((file, idx) => {
              const isImage = file.type.startsWith('image/');
              const previewUrl = isImage && blobUrlsRef ? blobUrlsRef.current.get(file) : null;

              return (
                <div key={idx} className="chatbot-attachment-item">
                  {isImage && previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={file.name}
                      className="attachment-thumbnail"
                    />
                  ) : (
                    <div className="attachment-icon">
                      {getFileTypeIcon(file.name, file.type)}
                    </div>
                  )}
                  <div className="attachment-info">
                    <span className="attachment-name">{file.name}</span>
                  </div>
                  <span className="attachment-separator"></span>
                  <button
                    onClick={() => handleRemoveFile(file)}
                    className="chatbot-remove-attachment"
                    aria-label="Remove file"
                    title="Remove file"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path fillRule="evenodd" clipRule="evenodd" d="M11.53 4.47a.75.75 0 0 0-1.06 0L8 6.94 5.53 4.47a.75.75 0 0 0-1.06 1.06L6.94 8l-2.47 2.47a.75.75 0 1 0 1.06 1.06L8 9.06l2.47 2.47a.75.75 0 1 0 1.06-1.06L9.06 8l2.47-2.47a.75.75 0 0 0 0-1.06Z" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <input
          type="text"
          value={value}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
        />
        
        {/* File upload button */}
        {onFilesChange && (
          <Upload
            accept={acceptedFileTypes}
            beforeUpload={handleBeforeUpload}
            showUploadList={false}
            multiple
            disabled={disabled}
          >
            <button
              className="chatbot-attach-button"
              disabled={disabled}
              title="Attach files (PDF, DOCX, HTML, TXT, Excel, CSV) or images (JPG, PNG, GIF, WebP, SVG)"
              aria-label="Attach files"
              type="button"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16.5,6V17.5A4,4 0 0,1 12.5,21.5A4,4 0 0,1 8.5,17.5V5A2.5,2.5 0 0,1 11,2.5A2.5,2.5 0 0,1 13.5,5V15.5A1,1 0 0,1 12.5,16.5A1,1 0 0,1 11.5,15.5V6H10V15.5A2.5,2.5 0 0,0 12.5,18A2.5,2.5 0 0,0 15,15.5V5A4,4 0 0,0 11,1A4,4 0 0,0 7,5V17.5A5.5,5.5 0 0,0 12.5,23A5.5,5.5 0 0,0 18,17.5V6H16.5Z"/>
              </svg>
            </button>
          </Upload>
        )}

        {(() => {
          const isSendDisabled = (!value.trim() && attachedFiles.length === 0) || disabled;
          return (
            <button
              className="reports-chatbot-send-button"
              onClick={handleSendClick}
              disabled={isSendDisabled}
              title="Send message"
              aria-label="Send message"
            >
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" rx="8" fill={isSendDisabled ? '#b2b2c7' : '#0141E4'}/>
                <path d="M16 23V9" stroke="#F7F7F7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M11 14L16 9L21 14" stroke="#F7F7F7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          );
        })()}
      </div>
    </div>
  );
};

export default ChatInputAreaWithUpload;
